/** Small DOM helpers. Enough structure to keep the screens readable, no framework. */

type Attrs = Record<string, string | number | boolean | ((e: Event) => void) | undefined>

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string | null | undefined)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue
    if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener)
    } else if (key === 'class') {
      node.className = String(value)
    } else if (key === 'text') {
      node.textContent = String(value)
    } else if (value === true) {
      node.setAttribute(key, '')
    } else {
      node.setAttribute(key, String(value))
    }
  }
  for (const child of children) {
    if (child === null || child === undefined) continue
    node.append(child)
  }
  return node
}

export function button(
  label: string,
  onClick: () => void,
  variant: 'primary' | 'secondary' | 'text' = 'secondary',
): HTMLButtonElement {
  return el('button', { class: `btn btn-${variant}`, type: 'button', onclick: onClick, text: label })
}

export function heading(text: string): HTMLElement {
  return el('h2', { class: 'section-heading', text })
}

/**
 * An announcement region. Messages go through here rather than alert() so a
 * screen reader hears them and so nothing blocks the signing flow.
 */
export function toast(message: string): void {
  let region = document.getElementById('toast')
  if (!region) {
    region = el('div', { id: 'toast', class: 'toast', role: 'status', 'aria-live': 'polite' })
    document.body.append(region)
  }
  region.textContent = message
  region.classList.add('visible')
  window.clearTimeout(Number(region.dataset.timer ?? 0))
  region.dataset.timer = String(
    window.setTimeout(() => region?.classList.remove('visible'), 6000),
  )
}

export function confirmDialog(title: string, message: string, confirmLabel: string): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = el('dialog', { class: 'dialog' }, [
      el('h2', { text: title }),
      el('p', { text: message }),
      el('div', { class: 'dialog-actions' }, [
        button('Cancel', () => {
          dialog.close()
          resolve(false)
        }),
        button(confirmLabel, () => {
          dialog.close()
          resolve(true)
        }, 'primary'),
      ]),
    ])
    dialog.addEventListener('close', () => dialog.remove())
    document.body.append(dialog)
    dialog.showModal()
  })
}
